import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { flushQueue } from "@/lib/offline-expense-flush";
import { loadQueue } from "@/lib/offline-expense-queue";
import { createExpenseAction } from "./actions/expenses";
import { run } from "@/lib/action-run";
import { RECONNECTED_EVENT } from "./api/client";

/**
 * 오프라인에 쌓아 둔 지출을 **연결되면 올린다.** 화면을 그리지 않는다.
 *
 * 왜 여기(여행 레이아웃)에 두는가: 밀린 항목을 올리는 일은 원래 비용 화면의
 * "동기화 대기" 섹션이 했는데, 그 섹션은 **비용 탭의 지출 하위 탭에 들어가야**
 * 마운트된다. 비행기에서 적어 둔 지출이 착륙 후에도 안 올라가는 이유가 그거였다
 * (실측). 여행 안에 있는 동안에는 어느 탭에 있든 시도한다.
 *
 * 두 번 보내는 걱정은 없다 — `flushQueue`가 자물쇠를 잡고, 항목마다 clientId로
 * 서버가 중복을 걸러낸다.
 */
export function OfflineQueueFlusher({ tripId }: { tripId: string }) {
  const flush = useCallback(async () => {
    if (loadQueue(tripId).length === 0) return;
    const result = await flushQueue(tripId, (t, input) => run(createExpenseAction(t, input)));
    if (result.flushed > 0) toast.success(`${result.flushed}건 동기화 완료`);
  }, [tripId]);

  useEffect(() => {
    void flush();

    // `online` 이벤트는 못 믿는다 — 기내 와이파이·캡티브 포털에서는 브라우저가
    // 내내 "온라인"이라 아예 안 온다. 요청 하나가 성공했다는 신호를 같이 듣고,
    // 그래도 못 받을 때를 대비해 남아 있는 동안 이따금 다시 본다.
    const again = () => void flush();
    window.addEventListener("online", again);
    window.addEventListener(RECONNECTED_EVENT, again);
    const id = setInterval(again, 20000);
    return () => {
      window.removeEventListener("online", again);
      window.removeEventListener(RECONNECTED_EVENT, again);
      clearInterval(id);
    };
  }, [flush]);

  return null;
}
